'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import type { AssistantConversation, SupportKind } from '@/lib/schemas/support.schema';
import { SupportButton } from './SupportButton';
import { SupportPanel } from './SupportPanel';
import type { DraftShot } from './ShotsField';
import { collector } from '@/lib/support/collector';
import type { LiveEventsHandle, SupportEvent } from '@/hooks/useLiveEvents';
import { parseTicketNumber } from '@/lib/schemas/support.schema';
import { LEGAL_PATHS } from '@/lib/legal';
import { assistantReducer, EMPTY_CONVERSATION, historyOf, type AssistantAction, type Conversation } from './assistant-state';
import { readAssistantStream } from './assistant-stream';
import { defaultTab, type SupportTab } from './default-tab';
import { draftFromAssistant } from './from-assistant';

// ── Le support dans toute l'application : le bouton, le panneau, le brouillon (chantier SUP) ──

export type { SupportTab };

export interface SupportDraft {
  kind: SupportKind;
  body: string;
  shots: DraftShot[];
  /** R4 : joindre la copie du projet a un probleme (coche par defaut) */
  attachCopy: boolean;
  /** La conversation avec l'assistant, jointe quand on passe de lui au support (chantier ASI, A12) */
  assistant?: AssistantConversation;
}

export const EMPTY_DRAFT: SupportDraft = { kind: 'BUG', body: '', shots: [], attachCopy: true };

/** Ce que le reste de l'application peut faire du support */
export interface SupportApi {
  /** Ouvre le panneau sur un onglet, ou sur le fil d'une demande */
  open: (tab?: SupportTab, thread?: number) => void;
  close: () => void;
  isOpen: boolean;
}

interface SupportContextValue extends SupportApi {
  /** Le support est offert sur cette page (R1) */
  visible: boolean;
  tab: SupportTab;
  draft: SupportDraft;
  setDraft: React.Dispatch<React.SetStateAction<SupportDraft>>;
  email: string;
  /** Le fil ouvert dans « Mes demandes », ou null pour la liste */
  thread: number | null;
  setTab: (tab: SupportTab) => void;
  setThread: (thread: number | null) => void;
  /** Mes reponses du support non lues : la pastille (R9) */
  unread: number;
  refreshUnread: () => void;
  /** La derniere reponse arrivee en direct (dans l'editeur) : le fil ouvert sur cette demande se relit */
  lastLive: { ticketNumber: number; at: number } | null;
  /** Une capture est en cours : le panneau se masque pour ne pas figurer sur l'image */
  capturing: boolean;
  setCapturing: (capturing: boolean) => void;
  /** Une reponse du support arrivee par le direct de l'editeur */
  onLive: (event: SupportEvent) => void;
  /** L'editeur monte : le bouton passe au-dessus de sa barre du bas, et se reduit pendant le dessin */
  setEditor: (editor: EditorPresence | null) => void;
  /** A1 : l'assistant est offert (en developpement seulement) */
  assistant: boolean;
  /** L'outil actif de l'editeur, ou undefined hors de l'editeur (A3) */
  editorTool: string | undefined;
  /** A10 : la conversation, gardee jusqu'au rechargement de la page */
  conversation: Conversation;
  askAssistant: (question: string) => void;
  stopAssistant: () => void;
  /** A12 : « Ecrire au support » depuis l'assistant : Nouvelle demande, preremplie */
  openFromAssistant: () => void;
  dispatchAssistant: (action: AssistantAction) => void;
}

interface EditorPresence {
  compact: boolean;
  /** L'outil actif : les suggestions de l'assistant le suivent (A3) */
  tool?: string;
}

/** A7 : au-dela de 2 min 30 de reveil, l'onglet propose le support ou d'annuler (Mistral Small : 49 s sur H100, plus sur la carte de repli) */
const WAKE_LONG_MS = 150_000;

const SupportContext = createContext<SupportContextValue | null>(null);

/**
 * R1 : pas de support sur les pages publiques (centre d'aide, pages legales, connexion), ni sur l'acceptation des CGU,
 * dans l'administration, ni dans la page de capture du studio video.
 */
const HIDDEN_PREFIXES = ['/admin', '/video-capture', '/aide', ...LEGAL_PATHS, '/conditions', '/login', '/register', '/forgot-password', '/reset-password', '/finalize-registration'];
export const supportVisibleOn = (pathname: string) =>
  !HIDDEN_PREFIXES.some(p => pathname === p || pathname.startsWith(`${p}/`));

export function SupportProvider({ children, assistant = false }: { children: React.ReactNode; assistant?: boolean }) {
  const { data: session, status } = useSession();
  const pathname = usePathname() ?? '/';
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState<SupportTab>('new');
  const [draft, setDraft] = useState<SupportDraft>(EMPTY_DRAFT);
  const [editor, setEditor] = useState<EditorPresence | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [thread, setThread] = useState<number | null>(null);
  const [unread, setUnread] = useState(0);
  const [lastLive, setLastLive] = useState<{ ticketNumber: number; at: number } | null>(null);
  const router = useRouter();
  const [conversation, dispatchAssistant] = useReducer(assistantReducer, EMPTY_CONVERSATION);
  const conversationRef = useRef(conversation);
  conversationRef.current = conversation;
  const abortRef = useRef<AbortController | null>(null);
  const editorRef = useRef<EditorPresence | null>(null);
  editorRef.current = editor;

  // Le collecteur ecoute la page des le chargement : les erreurs d'avant la demande comptent (R6)
  useEffect(() => { collector.install(window); }, []);

  const open = useCallback((next: SupportTab = 'new', ticket?: number) => {
    setTab(next);
    setThread(next === 'list' ? ticket ?? null : null);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  // L'utilisateur est connu tant que la session dure, meme pendant qu'elle se rafraichit
  const userId = session?.user?.id;
  const [signedIn, setSignedIn] = useState(false);
  useEffect(() => {
    if (status === 'authenticated' && userId) setSignedIn(true);
    if (status === 'unauthenticated') setSignedIn(false);
  }, [status, userId]);
  const visible = signedIn && supportVisibleOn(pathname);
  // Changer de page vers une page sans support ferme le panneau ; le brouillon reste
  useEffect(() => { if (!visible) setIsOpen(false); }, [visible]);

  // La pastille : lue au chargement et a chaque ouverture du panneau ; dans l'editeur, le direct la tient a jour
  const refreshUnread = useCallback(() => {
    fetch('/api/support/unread')
      .then(res => (res.ok ? res.json() as Promise<{ unread: number }> : null))
      .then(data => { if (data) setUnread(data.unread); })
      .catch(() => {});
  }, []);
  useEffect(() => { if (signedIn) refreshUnread(); }, [signedIn, refreshUnread]);
  useEffect(() => { if (isOpen) refreshUnread(); }, [isOpen, refreshUnread]);

  // Le lien du mail de reponse (?support=<n>) ouvre le fil de la demande, puis s'efface de l'adresse
  useEffect(() => {
    if (!visible) return;
    const params = new URLSearchParams(window.location.search);
    const number = parseTicketNumber(params.get('support') ?? '');
    if (number === null) return;
    open('list', number);
    params.delete('support');
    const rest = params.toString();
    router.replace(`${pathname}${rest ? `?${rest}` : ''}${window.location.hash}`, { scroll: false });
  }, [visible, pathname, open, router]);

  // L'assistant (chantier ASI) : la question part d'ici, pour qu'une reponse aboutisse meme panneau ferme
  const askAssistant = useCallback(async (question: string) => {
    const q = question.trim();
    const history = historyOf(conversationRef.current);
    dispatchAssistant({ type: 'ask', question: q });
    // L'indicateur IA de l'admin relit l'etat (ASI-E05)
    window.dispatchEvent(new Event('assistant:activite'));
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    let longTimer: ReturnType<typeof setTimeout> | undefined;
    try {
      const res = await fetch('/api/assistant/questions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q, history, page: { path: window.location.pathname, ...(editorRef.current?.tool ? { tool: editorRef.current.tool } : {}) } }),
        signal: ctrl.signal,
      });
      if (res.status === 429) {
        const data = await res.json().catch(() => ({})) as { error?: string };
        dispatchAssistant({ type: 'limit', message: data.error ?? 'La limite de questions du jour est atteinte.' });
        return;
      }
      if (!res.ok || !res.body) {
        dispatchAssistant({ type: 'event', event: { t: 'erreur', code: 'indisponible' } });
        return;
      }
      let firstWord = true;
      await readAssistantStream(res.body, event => {
        dispatchAssistant({ type: 'event', event });
        if (event.t === 'reveil') longTimer = setTimeout(() => dispatchAssistant({ type: 'wakeLong' }), WAKE_LONG_MS);
        // Le premier mot : le modele est reveille, l'indicateur de l'admin le montre tout de suite
        if (event.t === 'texte' && firstWord) {
          firstWord = false;
          window.dispatchEvent(new Event('assistant:activite'));
        }
      });
      // Un flux fini sans « fin » : la reponse s'est interrompue (sans effet si elle est finie)
      dispatchAssistant({ type: 'event', event: { t: 'erreur', code: 'interrompu' } });
    } catch {
      // Arreter (A8) a deja marque la reponse ; sinon, la connexion s'est perdue
      if (!ctrl.signal.aborted) dispatchAssistant({ type: 'event', event: { t: 'erreur', code: 'interrompu' } });
    } finally {
      clearTimeout(longTimer);
      if (abortRef.current === ctrl) abortRef.current = null;
      window.dispatchEvent(new Event('assistant:activite'));
    }
  }, []);

  const stopAssistant = useCallback(() => {
    dispatchAssistant({ type: 'stop' });
    abortRef.current?.abort();
  }, []);

  const openFromAssistant = useCallback(() => {
    setDraft(d => draftFromAssistant(d, conversationRef.current));
    setTab('new');
  }, []);

  const setLiveEvent = useCallback((event: SupportEvent) => {
    setUnread(event.unread);
    setLastLive({ ticketNumber: event.ticketNumber, at: Date.now() });
  }, []);

  const value = useMemo<SupportContextValue | null>(() => (signedIn ? {
    open, close, isOpen: isOpen && visible, visible, tab, draft, setDraft, email: session?.user?.email ?? '',
    thread, setTab, setThread, unread, refreshUnread, lastLive, onLive: setLiveEvent,
    capturing, setCapturing, setEditor,
    assistant, editorTool: editor?.tool, conversation, askAssistant, stopAssistant, dispatchAssistant, openFromAssistant,
  } : null), [signedIn, visible, open, close, isOpen, tab, draft, session?.user?.email, thread, unread, refreshUnread, lastLive, capturing,
    assistant, editor?.tool, conversation, askAssistant, stopAssistant, openFromAssistant]);

  return (
    <SupportContext.Provider value={value}>
      {children}
      {/* Le panneau reste monte pendant toute la session, meme sur une page sans support : un envoi en cours aboutit */}
      {value && <SupportPanel />}
      {/* A2 : l'assistant s'il est offert ; une reponse non lue mene a « Mes demandes » */}
      {visible && !isOpen && (
        <SupportButton onClick={() => open(defaultTab({ assistant, unread }))} inEditor={!!editor} compact={!!editor?.compact} unread={unread} />
      )}
    </SupportContext.Provider>
  );
}

/** Le support, ou null s'il n'est pas offert ici (non connecte, page publique, administration) */
export function useSupport(): SupportApi | null {
  const ctx = useContext(SupportContext);
  return ctx?.visible ? ctx : null;
}

/** A1 : l'assistant est-il offert ? (l'indicateur IA du header, ASI-E05) */
export function useAssistantEnabled(): boolean {
  return useContext(SupportContext)?.assistant ?? false;
}

/** Pour le panneau : l'etat complet */
export function useSupportPanel(): SupportContextValue {
  const ctx = useContext(SupportContext);
  if (!ctx) throw new Error('useSupportPanel hors de SupportProvider');
  return ctx;
}

/** L'editeur se signale au support tant qu'il est monte ; compact pendant qu'un outil de dessin est actif */
export function useSupportEditor(compact: boolean, tool?: string) {
  const setEditor = useContext(SupportContext)?.setEditor;
  useEffect(() => {
    if (!setEditor) return;
    setEditor({ compact, tool });
  }, [setEditor, compact, tool]);
  useEffect(() => {
    if (!setEditor) return;
    return () => setEditor(null);
  }, [setEditor]);
}

/** Dans l'editeur : les reponses du support arrivent par son flux en direct (la pastille, le fil ouvert) */
export function useSupportLive(live: LiveEventsHandle) {
  const onLive = useContext(SupportContext)?.onLive;
  useEffect(() => (onLive ? live.subscribe('support', onLive) : undefined), [live, onLive]);
}
